// src/redux/slices/drawers/opportunityDrawerSlice.ts
import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { patients, remote_care } from "lobbie";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants";
import { handleBackendHttpError } from "src/redux/handleBackendHttpError";
import { RootState } from "src/redux/store";
import { ERemoteCareOpportunityTabs } from "src/types/global_enums";
import { generateUUID, notify } from "src/utils";
import { createDrawerSlice, IBaseDrawerState } from "./createDrawerSlice";
import { loadInvoiceDrawer } from "./invoiceDrawerSlice";
import { createDrawerActionMeta, createDrawerThunkMeta } from "./stackableDrawerConstant";

export interface IOpportunityDrawerState extends IBaseDrawerState {
    opportunityId?: number;
    isLoading: boolean;
    remoteCareConversion: remote_care.IRemoteCareConversionFullDetails | null;
    accountPatient?: patients.IAccountPatient;
    opportunitySection: ERemoteCareOpportunityTabs;
}

export const initialRemoteCareOpportunityDrawerState: IOpportunityDrawerState = {
    drawerHistoryInstanceId: "",
    show: false,
    opportunityId: undefined,
    isLoading: false,
    remoteCareConversion: null,
    accountPatient: undefined,
    opportunitySection: ERemoteCareOpportunityTabs.DEMOGRAPHICS,
};

// Combined action to show drawer and load data
export const showOpportunityDrawer = createAsyncThunk<
    remote_care.IRemoteCareConversionFullDetails,
    { opportunityId: number },
    { state: RootState; rejectValue: string }
>(
    "opportunityDrawer/showOpportunityDrawer",
    async ({ opportunityId }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axios.get<remote_care.IRemoteCareConversionFullDetails>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/remote-care/conversion/${opportunityId}`,
                { withCredentials: true },
            );
            // After getting remote care conversion, load the account patient
            if (response.data?.accountPatientId) {
                await dispatch(
                    loadAccountPatient({ accountPatientId: response.data.accountPatientId }),
                );
            }

            return response.data;
        } catch (error) {
            handleBackendHttpError(error);
            notify({
                title: "Error",
                message: "Failed to fetch Opportunity",
                level: "error",
            });
            return rejectWithValue("Failed to fetch Opportunity");
        }
    },
    createDrawerThunkMeta("show"),
);

export const loadAccountPatient = createAsyncThunk<
    patients.IAccountPatient,
    { accountPatientId: number },
    { state: RootState; rejectValue: string }
>("opportunityDrawer/loadAccountPatient", async ({ accountPatientId }, { rejectWithValue }) => {
    try {
        const response = await axios.get<patients.IAccountPatient>(
            `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/account_patients/${accountPatientId}`,
            { withCredentials: true },
        );
        return response.data;
    } catch (error) {
        handleBackendHttpError(error);
        notify({
            title: "Error",
            message: "Failed to fetch Account Patient",
            level: "error",
        });
        return rejectWithValue("Failed to fetch Account Patient");
    }
});

export const updateAccountPatient = createAsyncThunk<
    patients.IAccountPatient,
    { accountPatientId: number; answerValues: any[] },
    { state: RootState; rejectValue: string }
>(
    "opportunityDrawer/updateAccountPatient",
    async ({ accountPatientId, answerValues }, { rejectWithValue }) => {
        try {
            // const state = getState();
            // const account = new Account(state.account.account as Account);
            // const lowerPatientFieldName = account?.lowerPatientFieldName;
            const data = { anwerValues: answerValues };
            const response = await axios.put<patients.IAccountPatient>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/account_patients/${accountPatientId}`,
                data,
                { withCredentials: true },
            );
            return response.data;
        } catch (error) {
            handleBackendHttpError(error);
            return rejectWithValue("Failed to update Account Patient");
        }
    },
);

export const setOpportunityTabAndLoad = createAsyncThunk<
    void,
    { section: ERemoteCareOpportunityTabs },
    { state: RootState }
>("opportunityDrawer/setOpportunityTabAndLoad", async ({ section }, { dispatch, getState }) => {
    // If we're switching to the purchase section and need to load invoice
    const state = getState();
    if (
        section === ERemoteCareOpportunityTabs.PURCHASE &&
        state.remoteCareOpportunityDrawer.remoteCareConversion?.invoiceId
    ) {
        await dispatch(
            loadInvoiceDrawer({
                invoiceId: state.remoteCareOpportunityDrawer.remoteCareConversion.invoiceId,
                show: false, // Don't show the drawer itself
            }),
        );
    }
});

const remoteCareOpportunityDrawerSlice = createDrawerSlice({
    name: "remoteCareOpportunityDrawer",
    initialState: initialRemoteCareOpportunityDrawerState,
    reducers: {
        setOpportunityDrawerData: (
            state,
            action: PayloadAction<Partial<IOpportunityDrawerState>>,
        ) => {
            return { ...state, ...action.payload };
        },
        setOpportunitySection: (state, action: PayloadAction<ERemoteCareOpportunityTabs>) => {
            state.opportunitySection = action.payload;
        },
        hideRemoteCareOpportunityDrawer: {
            prepare: () => ({
                payload: undefined,
                meta: createDrawerActionMeta("hide"),
            }),
            reducer: (state) => {
                state.show = false;
            },
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(showOpportunityDrawer.pending, (state, action) => {
                // Initialize the drawer state in the pending case
                state.drawerHistoryInstanceId = `opportunityDrawer-${generateUUID()}`;
                state.show = true;
                state.opportunityId = action.meta.arg.opportunityId;
                state.isLoading = true;
            })
            .addCase(showOpportunityDrawer.fulfilled, (state, action) => {
                state.isLoading = false;
                state.remoteCareConversion = action.payload;
            })
            .addCase(showOpportunityDrawer.rejected, (state) => {
                state.isLoading = false;
                // Consider hiding the drawer on rejection
                state.show = false;
            })
            .addCase(loadAccountPatient.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(loadAccountPatient.fulfilled, (state, action) => {
                state.accountPatient = action.payload;
                state.isLoading = false;
            })
            .addCase(loadAccountPatient.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateAccountPatient.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateAccountPatient.fulfilled, (state, action) => {
                state.accountPatient = action.payload;
                state.isLoading = false;
            })
            .addCase(updateAccountPatient.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(setOpportunityTabAndLoad.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(setOpportunityTabAndLoad.fulfilled, (state, action) => {
                state.isLoading = false;
                state.opportunitySection = action.meta.arg.section;
            })
            .addCase(setOpportunityTabAndLoad.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const { hideRemoteCareOpportunityDrawer, setOpportunityDrawerData } =
    remoteCareOpportunityDrawerSlice.actions;

export default remoteCareOpportunityDrawerSlice.reducer;
